import {
  Box,
  Grid,
  Paper,
  Stack,
  TableBody,
  TableContainer,
  TableHead,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { limit } from 'firebase/firestore';
import {
  grid,
  paper,
  StyledSkeleton,
  titleStyles,
} from '../DashboardInventorySearchReport/DashboardInventorySearchReport.styles';
import { StyledTable, StyledTableCell, StyledTableRow } from '../../tables';
import { useMilestones } from '../../hooks/useMilestones';
import {
  Milestone,
  MilestoneCountConversionByCompany,
  MilestoneCountConversionByUser,
  MilestoneCountReservationByCompany,
  MilestoneCountReservationByUser,
  MilestoneTypes,
} from '../../models/Milestone';

interface MilestoneRow {
  date: string;
  message: string;
}

interface Props {
  loading: boolean;
}

export const DashboardMilestonesReport = ({
  loading: dashboardIsLoading,
}: Props) => {
  const { t } = useTranslation();

  const { list: milestones, loading } = useMilestones([limit(30)]);

  const getMilestoneMessage = useCallback(
    (milestone: Milestone) => {
      switch (milestone.type) {
        case MilestoneTypes.CountReservationByUser:
          return t('dashboard.milestonesReport.count.reservationsByUser', {
            username: (milestone as MilestoneCountReservationByUser).username,
            companyName: (milestone as MilestoneCountReservationByUser)
              .companyName,
            count: (milestone as MilestoneCountReservationByUser).count,
          });
        case MilestoneTypes.CountReservationByCompany:
          return t('dashboard.milestonesReport.count.reservationsByCompany', {
            companyName: (milestone as MilestoneCountReservationByCompany)
              .companyName,
            count: (milestone as MilestoneCountReservationByCompany).count,
          });
        case MilestoneTypes.CountConversionByUser:
          return t('dashboard.milestonesReport.count.conversionsByUser', {
            username: (milestone as MilestoneCountConversionByUser).username,
            companyName: (milestone as MilestoneCountConversionByUser)
              .companyName,
            count: (milestone as MilestoneCountConversionByUser).count,
          });
        case MilestoneTypes.CountConversionByCompany:
          return t('dashboard.milestonesReport.count.conversionsByCompany', {
            companyName: (milestone as MilestoneCountConversionByCompany)
              .companyName,
            count: (milestone as MilestoneCountConversionByCompany).count,
          });
        default:
          console.error(
            'Uncaught error:',
            new Error('Milestone type not recognized')
          );
          return '';
      }
    },
    [t]
  );

  const data: MilestoneRow[] = useMemo(() => {
    if (loading || !milestones) {
      return [];
    }

    return milestones.map(
      (milestone: Milestone) =>
        ({
          date: milestone.dateCreated.toDate().toDateString(),
          message: getMilestoneMessage(milestone),
        } as MilestoneRow)
    );
  }, [getMilestoneMessage, loading, milestones]);

  const buildHeader = () => (
    <StyledTableRow>
      <StyledTableCell key="date">
        <Typography variant="body1">
          {t('dashboard.milestonesReport.date')}
        </Typography>
      </StyledTableCell>
      <StyledTableCell key="milestone">
        <Typography variant="body1">
          {t('dashboard.milestonesReport.milestone')}
        </Typography>
      </StyledTableCell>
    </StyledTableRow>
  );

  const buildRow = (milestone: MilestoneRow, index: number) => (
    <StyledTableRow
      key={index}
      className={`${index % 2 === 1 ? 'alternate-color' : ''}`}
    >
      <StyledTableCell key="date">
        <Typography variant="body1">{milestone.date}</Typography>
      </StyledTableCell>
      <StyledTableCell key="milestone">
        <Typography variant="body1">{milestone.message}</Typography>
      </StyledTableCell>
    </StyledTableRow>
  );

  return (
    <Paper elevation={1} sx={paper}>
      <Grid sx={grid}>
        {!dashboardIsLoading && !loading ? (
          <>
            <Typography variant="h4" sx={titleStyles}>
              {t('dashboard.milestonesReport.title')}
            </Typography>

            {data.length === 0 ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h2">{t('common.noItemsFound')}</Typography>
              </Box>
            ) : (
              <TableContainer sx={{ mb: 3 }}>
                <StyledTable sx={{ width: '100%' }}>
                  <TableHead>{buildHeader()}</TableHead>
                  <TableBody>
                    {data.map((milestone, index) => buildRow(milestone, index))}
                  </TableBody>
                </StyledTable>
              </TableContainer>
            )}
          </>
        ) : (
          <Stack spacing={1} direction="column">
            <StyledSkeleton
              animation="wave"
              variant="rectangular"
              height={50}
            />
            <StyledSkeleton
              animation="wave"
              variant="rectangular"
              height={400}
            />
          </Stack>
        )}
      </Grid>
    </Paper>
  );
};
